import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessingControlPanel = ({ 
  isProcessing, 
  isPaused, 
  canViewResults, 
  onPause, 
  onResume, 
  onCancel, 
  onViewResults 
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    onCancel();
    setShowCancelDialog(false);
  };

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false);
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-foreground mb-4">Processing Controls</h3>
        
        <div className="space-y-4">
          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {isProcessing && !isPaused && (
              <Button
                variant="warning"
                onClick={onPause}
                iconName="Pause"
                iconPosition="left"
                className="flex-1"
              >
                Pause Processing
              </Button>
            )}

            {isPaused && (
              <Button
                variant="success"
                onClick={onResume}
                iconName="Play"
                iconPosition="left"
                className="flex-1"
              >
                Resume Processing
              </Button>
            )}

            {canViewResults && (
              <Button
                variant="outline"
                onClick={onViewResults}
                iconName="BarChart3"
                iconPosition="left"
                className="flex-1"
              >
                View Preliminary Results
              </Button>
            )}
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="destructive"
              onClick={handleCancelClick}
              iconName="X"
              iconPosition="left"
              className="flex-1"
            >
              Cancel Operation
            </Button>

            <Button
              variant="ghost"
              onClick={() => window.location.href = '/column-mapping-interface'}
              iconName="ArrowLeft"
              iconPosition="left"
              className="flex-1"
            >
              Back to Configuration
            </Button>
          </div>

          {/* Status Information */}
          <div className="pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <div className="flex items-center space-x-2">
                    {isProcessing && !isPaused && (
                      <>
                        <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                        <span className="font-medium text-success">Processing</span>
                      </>
                    )}
                    {isPaused && (
                      <>
                        <div className="w-2 h-2 bg-warning rounded-full" />
                        <span className="font-medium text-warning">Paused</span>
                      </>
                    )}
                    {!isProcessing && !isPaused && (
                      <>
                        <div className="w-2 h-2 bg-muted-foreground rounded-full" />
                        <span className="font-medium text-muted-foreground">Idle</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Started:</span>
                  <span className="font-medium text-foreground">
                    {new Date()?.toLocaleTimeString('en-US', {
                      hour12: false,
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Priority:</span>
                  <span className="font-medium text-foreground">High</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Auto-save:</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Check" size={12} className="text-success" />
                    <span className="font-medium text-success">Enabled</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground mb-2">Quick Actions:</div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={() => window.location.href = '/export-and-reports-center'}
              >
                Export Logs
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Status
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                iconPosition="left"
              >
                Adjust Settings
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-error bg-opacity-10 rounded-full">
                <Icon name="AlertTriangle" size={20} className="text-error" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Cancel Processing?</h3>
            </div>

            <p className="text-muted-foreground mb-6">
              Are you sure you want to cancel the current processing operation? All progress will be lost and you'll need to restart from the beginning.
            </p>

            <div className="flex space-x-3">
              <Button
                variant="destructive"
                onClick={handleConfirmCancel}
                iconName="X"
                iconPosition="left"
                className="flex-1"
              >
                Yes, Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelDialogClose}
                className="flex-1"
              >
                Keep Processing
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcessingControlPanel;